import {
    ActionIcon,
    Alert,
    Badge,
    Divider,
    Drawer,
    Group,
    Image,
    Paper,
    Popover,
    Stack,
    Table,
    Text,
    Title, Tooltip,
} from "@mantine/core";
import {useMutation, useQuery} from "@tanstack/react-query";
import {DataTable} from "mantine-datatable";
import {DateTime} from "luxon";
import {useContext, useState} from "react";
import {useParams} from "react-router";
import withRouteErrorBoundary from "../../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import LoaderScreen from "../../../components/Loader/LoaderScreen.tsx";
import Page from "../../../components/Page/Page.tsx";
import api, {useEntityTypes} from "../../../utils/api.ts";
import ProductionModuleNav from "./ProductionModuleNav.tsx";
import {useProductionModule} from "./ProductionModuleContext.tsx";
import {ProductionDetailsResponse, ProductionEntityDetailsResponse, ProductionMaterialItem, ProductionTrackedEntity} from "./types.ts";
import classes from "./ProductionModuleDetailsPage.module.css";
import useCivLevelMap from "../../../utils/production-estimate/useCivLevelMap.ts";
import {resolveCivLevel} from "../../../utils/production-estimate";
import {EMPTY_RECORD, formatDuration, getLocationLabel, castString, getRecord} from "./shared.ts";
import {ArrowsClockwiseIcon, CheckCircleIcon, InfoIcon, WarningIcon} from "@phosphor-icons/react";
import {useDisclosure} from "@mantine/hooks";
import MessageContext from "../../../context/MessageContext.tsx";

interface EntityReference {
    id: string;
    name: string;
    type: string;
    href: string;
}

interface MaterialContainerGroup {
    key: string;
    containerName: string;
    containerId: string;
    systemName: string;
    quantity: number;
    piles: number;
    materials: ProductionMaterialItem[];
}

const AVG_EFFICIENCY = 0.9;

function getEntityReference(data: Record<string, unknown> | undefined, key: string): EntityReference | null {
    if (!data || !data[key]) {
        return null;
    }

    const row = getRecord(data, key);
    const id = castString(row, "id");
    const name = castString(row, "name");
    if (id === "" && name === "") {
        return null;
    }
    return {
        id,
        name,
        type: castString(row, "type"),
        href: castString(row, "href"),
    };
}

function formatNumber(value: unknown): string {
    if(typeof value === 'string') {
        value = parseInt(value)
    }
    if (typeof value !== 'number') {
        return '-'
    }
    return new Intl.NumberFormat().format(value);
}

function formatTimeWindow(total: number | null | undefined, remaining: number | null | undefined): string {
    const hasRemaining = remaining !== null && remaining !== undefined && Number.isFinite(Number(remaining));
    const hasTotal = total !== null && total !== undefined && Number.isFinite(Number(total));
    if (!hasRemaining && !hasTotal) {
        return "-";
    }
    if (hasRemaining && hasTotal) {
        return `${formatDuration(remaining)} remaining / ${formatDuration(total)} total`;
    }
    return formatDuration(hasRemaining ? remaining : total);
}

function formatTime(seconds: number | null | undefined): string {
    if (!seconds) {
        return "-";
    }
    return DateTime.fromSeconds(seconds).toFormat("yyyy-LL-dd HH:mm");
}

function capitalize(value: string): string {
    if (!value) {
        return value;
    }
    return `${value[0].toUpperCase()}${value.slice(1)}`;
}

function formatProducingSummary(entity: ProductionTrackedEntity): string {
    const producing = entity.currentAction?.productTypeName ?? '';
    if (producing === '') {
        return "None";
    }
    return `${entity.currentAction?.quantity}x ${producing}`;
}

function resolveTrackedEntityTypeName(
    entity: ProductionTrackedEntity,
    stationTypeNames: Map<string, string>,
    facilityTypeNames: Map<string, string>
): string {
    const entityData = entity.entityData ?? EMPTY_RECORD;
    const typeId = castString(entityData, "typeId");
    const typeName = castString(entityData, "typeName");

    if (entity.entityType === "station" && typeId && stationTypeNames.has(typeId)) {
        return stationTypeNames.get(typeId) || typeName || "Station";
    }
    if (entity.entityType === "facility" && typeId && facilityTypeNames.has(typeId)) {
        return facilityTypeNames.get(typeId) || typeName || "Facility";
    }
    if (typeName) {
        return typeName;
    }

    return capitalize(entity.entityType || "entity");
}




function getMaterialGroups(materials: ProductionMaterialItem[]): MaterialContainerGroup[] {
    const grouped = new Map<string, MaterialContainerGroup>();

    materials.forEach((material, index) => {
        const location = material.location;
        const containerId = castString(location, "containerId");
        const containerName = castString(location, "containerName") || containerId;
        const systemName = castString(location, "systemName") || castString(location, "systemId");
        const key = `${containerId || containerName}:${systemName}:${index}`;
        const uniqueKey = `${containerId || containerName}:${systemName}`;

        const existing = grouped.get(uniqueKey) ?? {
            key,
            containerName,
            containerId,
            systemName,
            quantity: 0,
            piles: 0,
            materials: [],
        };

        const quantity = Number(material.quantity);
        existing.quantity += Number.isFinite(quantity) ? quantity : 0;
        existing.piles += 1;
        existing.materials.push(material);
        grouped.set(uniqueKey, existing);
    });

    return Array.from(grouped.values()).sort((a, b) => {
        const systemCmp = a.systemName.localeCompare(b.systemName);
        if (systemCmp !== 0) {
            return systemCmp;
        }
        return a.containerName.localeCompare(b.containerName);
    });
}

function renderReference(reference: EntityReference | null, emptyLabel: string) {
    if (!reference) {
        return <Text size="sm" c="dimmed">{emptyLabel}</Text>;
    }

    return (
        <Text size="sm">
            {reference.name || "Unknown"}
        </Text>
    );
}

const max3Digits = new Intl.NumberFormat('en', {
    maximumFractionDigits: 3
})

function efficiencyBody (entity: ProductionTrackedEntity) {
    const civLevel = useCivLevelMap();
    const [opened, { close, open }] = useDisclosure(false);
    if(civLevel.data) {
        const civ = resolveCivLevel(entity.location, civLevel.data)/100;
        const civFactor = Math.max(0, 1 - civ / 8);
        const locationMod = entity.entityType === 'facility' ? 1 : 0.9;

        const eff =  civFactor * locationMod;
        const rate = (AVG_EFFICIENCY / eff) * 100;

        let color = 'yellow.8';
        if(rate > 100) {
            color = 'green.8'
        } else if (rate < 100) {
            color = 'red.8';
        }

        return <Popover withArrow opened={opened}>
            <Group justify="end">
            <Text size="sm" c={color}>{max3Digits.format(rate)}% </Text>
            <Popover.Target><InfoIcon onMouseEnter={open} onMouseLeave={close} /></Popover.Target>
            <Popover.Dropdown>
                <Text size="sm">Civ level: {max3Digits.format(civ)}</Text>
                <Text size="sm">Civ factor: {max3Digits.format(civFactor)}</Text>
                <Text size="sm">Location: {locationMod}</Text>
                <Text size="sm" fw="bold">Multiplier: {max3Digits.format(eff)}</Text>
            </Popover.Dropdown>
            </Group>
        </Popover>;
    }
}

function ProductionModuleDetailsPage() {
    const {moduleId} = useParams<{moduleId?: string}>();
    const moduleInfo = useProductionModule();
    const {success, error} = useContext(MessageContext);
    const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);

    const query = useQuery<ProductionDetailsResponse>({
        queryKey: ["prod-module", moduleId, "details"],
        queryFn: async () => (await api.get(`/modules/production/${moduleId}/details`)).data as ProductionDetailsResponse,
        enabled: !!moduleId,
    });

    const entityDetailsQuery = useQuery<ProductionEntityDetailsResponse>({
        queryKey: ["prod-module", moduleId, "details", selectedEntityId],
        queryFn: async () => (await api.get(`/modules/production/${moduleId}/entities/${selectedEntityId}/details`)).data as ProductionEntityDetailsResponse,
        enabled: !!moduleId && !!selectedEntityId,
    });
    const refreshMutation = useMutation({
        mutationFn: async (row: ProductionTrackedEntity) => {
            return (await api.post(`/modules/production/${moduleId}/entities/${row.entityId}/refresh`, {entityType: row.entityType})).data;
        },
        onSuccess: () => {
            success('Entity refreshed');
            query.refetch()
        },
        onError: (e: any) => {
            error('Refresh failed', e?.response?.data?.error ?? e.message)
            query.refetch()
        },
    });

    const {stations, facilities} = useEntityTypes(["stations", "facilities"]);

    if (!moduleId) {
        return <Page><Alert color="red" title="Missing module ID">No module selected.</Alert></Page>;
    }
    if (query.isPending) {
        return <LoaderScreen message="Loading details"/>;
    }
    if (query.isError || !query.data) {
        return <Page><Alert color="red" title="Details unavailable">Could not load production details.</Alert></Page>;
    }

    const entities = query.data.entities;
    const stationTypeNames = new Map(stations.map((type) => [type.id, type.name]));
    const facilityTypeNames = new Map(facilities.map((type) => [type.id, type.name]));

    const lastSyncBody = (row: ProductionTrackedEntity) => {
        const status = row.syncStatus === 'ok' ?
          <CheckCircleIcon color="green" /> :
          <Tooltip label={row.syncStatus}><WarningIcon color="red" /></Tooltip>;

        const lastSync = row.lastSyncAt ? DateTime.fromSeconds(row.lastSyncAt) : null
        const time = lastSync ? lastSync.toRelative() : "-";

        let color = 'dimmed';
        if(lastSync) {
            const diff = lastSync.diffNow().as('hours');

            if(diff < -48)
                color = 'red'
            else if(diff < -24)
                color = 'orange'
        }

        if(time && time)

        return <Group justify="space-between">
            <Group>
                {status}
            <Text size="sm" c={color}>{time}</Text>
            </Group>
            <ActionIcon variant="light" title="Refresh" onClick={(e) => {
                refreshMutation.mutate(row)
                e.stopPropagation();
            }}><ArrowsClockwiseIcon /></ActionIcon>
        </Group>;
    };

    const actionBody = (row: ProductionTrackedEntity) => {
        if (!row.currentAction) {
            return "None";
        }
        return formatProducingSummary(row);
    };

    const detailEntity = entityDetailsQuery.data?.entity;
    const detailEntityData = detailEntity?.entityData ?? EMPTY_RECORD;
    const location = detailEntity?.location ?? EMPTY_RECORD;
    const images = getRecord(detailEntityData, "images");
    const imageSrc = castString(images, "customLarge")
        || castString(images, "large")
        || castString(images, "customSmall")
        || castString(images, "small");
    const pilot = getEntityReference(detailEntityData, "pilot");
    const commander = getEntityReference(detailEntityData, "commander");
    const owner = getEntityReference(detailEntityData, "owner");
    const materialGroups = getMaterialGroups(entityDetailsQuery.data?.materials ?? []);
    const detailGroups = detailEntity?.groups ?? [];
    const cargo = getRecord(detailEntityData, "cargo");

    return (
        <Page>
            <title>{moduleInfo?.label || 'Production'} - Details</title>
            <Stack gap="md">
                <Title order={1}>{moduleInfo?.label || 'Production'} - Details</Title>
                <ProductionModuleNav moduleId={moduleId}/>

                <DataTable
                    records={entities}
                    idAccessor="entityId"
                    rowClassName={() => classes.clickableRow}
                    onRowClick={({record}) => setSelectedEntityId(record.entityId)}
                    columns={[
                        {accessor: "entityId", title: "ID", render: row => <Text size="xs">{row.entityId}</Text>},
                        {accessor: "name", title: "Entity"},
                        {
                            accessor: "entityType",
                            title: "Type",
                            render: (row) => resolveTrackedEntityTypeName(row, stationTypeNames, facilityTypeNames),
                        },
                        {accessor: "groups", title: "Groups", render: (row) => row.groups?.map((group) => group.name).join(", ") || "-"},
                        {accessor: "currentAction", title: "Current Action", render: actionBody},
                        {accessor: "queuedItems", title: "Queue", render: (row) => row.queuedItems.length},
                        {accessor: 'efficiency', title: 'Efficiency', render: efficiencyBody},
                        {accessor: "lastSyncAt", title: "Last Sync", render: lastSyncBody},
                    ]}
                />
            </Stack>

            <Drawer
                opened={!!selectedEntityId}
                onClose={() => setSelectedEntityId(null)}
                position="right"
                size="lg"
                title={detailEntity ? `${detailEntity.name} (${detailEntity.entityId})` : "Entity details"}
            >
                {entityDetailsQuery.isPending ? (
                    <Text size="sm" c="dimmed">Loading entity details...</Text>
                ) : null}
                {entityDetailsQuery.isError ? (
                    <Alert color="red" title="Entity details unavailable">Could not load entity details.</Alert>
                ) : null}
                {detailEntity ? (
                    <Stack gap="md">
                        <Paper withBorder p="sm">
                            <Group align="flex-start" wrap="nowrap">
                                {imageSrc ? (
                                    <Image src={imageSrc} alt={detailEntity.name} w={128} radius="sm"/>
                                ) : null}
                                <Stack gap={4}>
                                    <Text fw={700}>{detailEntity.name}</Text>
                                    <Text size="sm" c="dimmed">
                                        {resolveTrackedEntityTypeName(detailEntity, stationTypeNames, facilityTypeNames)} | {detailEntity.entityId}
                                    </Text>
                                    <Text size="sm">Location: {getLocationLabel(location) || "-"}</Text>
                                    <Text size="sm">Last Sync: {formatTime(detailEntity.lastSyncAt)}</Text>
                                    <Text size="sm">Sync: {detailEntity.syncStatus}</Text>
                                </Stack>
                            </Group>
                        </Paper>

                        <Paper withBorder p="sm">
                            <Text fw={700}>Groups</Text>
                            <Divider my="xs"/>
                            {detailGroups.length > 0 ? (
                                <Group gap="xs">
                                    {detailGroups.map((group) => (
                                        <Badge key={group.id} variant="light">{group.name}</Badge>
                                    ))}
                                </Group>
                            ) : (
                                <Text size="sm" c="dimmed">No groups assigned.</Text>
                            )}
                        </Paper>

                        <Paper withBorder p="sm">
                            <Text fw={700}>Crew & Control</Text>
                            <Divider my="xs"/>
                            <Text size="xs" c="dimmed">Pilot</Text>
                            {renderReference(pilot, "No pilot")}
                            <Text size="xs" c="dimmed" mt="xs">Commander</Text>
                            {renderReference(commander, "No commander")}
                            <Text size="xs" c="dimmed" mt="xs">Owner</Text>
                            {renderReference(owner, "No owner")}
                        </Paper>

                        <Paper withBorder p="sm">
                            <Text fw={700}>Current Action</Text>
                            <Divider my="xs"/>
                            {detailEntity.currentAction ? (
                                <Stack gap={4}>
                                    <Text size="sm">Status: {detailEntity.currentAction.status || "-"}</Text>
                                    <Text size="sm">Quantity: {detailEntity.currentAction.quantity}</Text>
                                    <Text size="sm">Workers: {detailEntity.currentAction.workers}</Text>
                                    <Text size="sm">Time: {formatTimeWindow(detailEntity.currentAction.delayTotal, detailEntity.currentAction.delayRemaining)}</Text>
                                    <Text size="sm">Ends at: {formatTime(detailEntity.currentAction.endAt)}</Text>
                                    <Text size="sm">
                                        Producing: {formatProducingSummary(detailEntity)}
                                    </Text>
                                </Stack>
                            ) : (
                                <Text size="sm" c="dimmed">No running production action.</Text>
                            )}
                            <Text size="sm" mt={4}>
                                Tooled to: {detailEntity.tooledEntityName || <i>Nothing</i>}
                            </Text>
                        </Paper>

                        <Paper withBorder p="sm">
                            <Text fw={700}>Capacity Status</Text>
                            <Divider my="xs"/>
                            <Stack gap={4}>
                                <Text size="sm">
                                    Passengers: {formatNumber(cargo.passengerUsed)} / {formatNumber(cargo.passengerTotal)}
                                    {cargo.passengerRemaining !== null ? ` (remaining ${formatNumber(cargo.passengerRemaining)})` : ""}
                                </Text>
                                <Text size="sm">
                                    Weight Status: {formatNumber(cargo.weightUsed)} / {formatNumber(cargo.weightTotal)}
                                    {cargo.weightRemaining !== null ? ` (remaining ${formatNumber(cargo.weightRemaining)})` : ""}
                                </Text>
                                <Text size="sm">
                                    Volume Status: {formatNumber(cargo.volumeUsed)} / {formatNumber(cargo.volumeTotal)}
                                    {cargo.volumeRemaining !== null ? ` (remaining ${formatNumber(cargo.volumeRemaining)})` : ""}
                                </Text>
                            </Stack>
                        </Paper>

                        <Paper withBorder p="sm">
                            <Text fw={700}>Queue ({detailEntity.queuedItems.length})</Text>
                            <Divider my="xs"/>
                            <Table.ScrollContainer minWidth={200}>
                            <Table withTableBorder>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Order</Table.Th>
                                        <Table.Th>Entity Type</Table.Th>
                                        <Table.Th>Product Name</Table.Th>
                                        <Table.Th>Status</Table.Th>
                                        <Table.Th>Qty</Table.Th>
                                        <Table.Th>Workers</Table.Th>
                                        <Table.Th>Time</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {detailEntity.queuedItems.map((item, index) => (
                                        <Table.Tr key={`${item.queueOrder}:${item.entityId}:${index}`}>
                                            <Table.Td>{item.queueOrder}</Table.Td>
                                            <Table.Td>{item.entityName || "-"}</Table.Td>
                                            <Table.Td>{item.name || "-"}</Table.Td>
                                            <Table.Td>{item.status || "-"}</Table.Td>
                                            <Table.Td>{item.quantity}</Table.Td>
                                            <Table.Td>{item.workers}</Table.Td>
                                            <Table.Td>{formatTimeWindow(item.delayTotal, item.delayRemaining)}</Table.Td>
                                        </Table.Tr>
                                    ))}
                                    {detailEntity.queuedItems.length === 0 ? (
                                        <Table.Tr>
                                            <Table.Td colSpan={7}><Text size="sm" c="dimmed">Queue is empty.</Text></Table.Td>
                                        </Table.Tr>
                                    ) : null}
                                </Table.Tbody>
                            </Table>
                            </Table.ScrollContainer>
                        </Paper>

                        <Paper withBorder p="sm">
                            <Text fw={700}>
                                Materials ({formatNumber(entityDetailsQuery.data?.materialsCount ?? 0)} piles, {formatNumber(entityDetailsQuery.data?.materialsQuantity ?? 0)} total qty)
                            </Text>
                            <Text size="xs" c="dimmed">Materials sync: {formatTime(entityDetailsQuery.data?.materialsLastSyncAt)}</Text>
                            <Divider my="xs"/>

                            {materialGroups.length === 0 ? (
                                <Text size="sm" c="dimmed">No materials matched this entity location.</Text>
                            ) : (
                                <Stack gap="sm">
                                    {materialGroups.map((group) => (
                                        <Paper withBorder p="xs" key={group.key}>
                                            <Text size="sm" fw={600}>
                                                {group.containerName}{group.containerId ? ` (${group.containerId})` : ""}
                                            </Text>
                                            <Text size="xs" c="dimmed">
                                                {group.systemName} | {formatNumber(group.quantity)} qty | {formatNumber(group.piles)} piles
                                            </Text>
                                            <Table.ScrollContainer minWidth={200}>
                                            <Table withTableBorder mt="xs">
                                                <Table.Thead>
                                                    <Table.Tr>
                                                        <Table.Th>Pile Name</Table.Th>
                                                        <Table.Th>Material</Table.Th>
                                                        <Table.Th>Qty</Table.Th>
                                                    </Table.Tr>
                                                </Table.Thead>
                                                <Table.Tbody>
                                                    {group.materials.map((material) => (
                                                        <Table.Tr key={material.materialId}>
                                                            <Table.Td>{material.pileName}</Table.Td>
                                                            <Table.Td>{material.materialName || "-"}</Table.Td>
                                                            <Table.Td>{formatNumber(Number(material.quantity) || 0)}</Table.Td>
                                                        </Table.Tr>
                                                    ))}
                                                </Table.Tbody>
                                            </Table>
                                            </Table.ScrollContainer>
                                        </Paper>
                                    ))}
                                </Stack>
                            )}
                        </Paper>
                    </Stack>
                ) : null}
            </Drawer>
        </Page>
    );
}

export default withRouteErrorBoundary(ProductionModuleDetailsPage);
